import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAdminApps, type AdminRegistryApp } from '@/api/console';
import {
    Button,
    EmptyState,
    ErrorState,
    PageHeader,
    SectionCard,
    SkeletonTable,
} from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { AppsTable, APPS_TABLE_BASE_COLUMNS } from './apps/AppsTable';
import { AppsToolbar } from './apps/AppsToolbar';
import { FlipAppModal } from './apps/FlipAppModal';
import { AppOnboardingModal } from './apps/AppOnboardingModal';
import { AppPermissionGroupsModal } from './apps/AppPermissionGroupsModal';
import { CatalogSyncPreviewModal } from './apps/CatalogSyncPreviewModal';
import { CreateAppRoleModal } from './appRoles/CreateAppRoleModal';
import { useAppsTable } from './apps/useAppsTable';

/**
 * EAA-1819/EAA-1817: `/console/apps` — Danh mục ứng dụng (the D11 app
 * registry), rebuilt per design frame S6 on top of the EAA-1776/1812 screen
 * and extended per frames 45-48 (registry filters/sort, register-app fields,
 * OIDC client credentials, onboarding checklist).
 *
 * New apps are unapproved and invisible in every launcher; the enable switch
 * here IS the production approval gate, so it shares the same
 * destructive-style confirm as disable (see `FlipAppModal`) and warns when
 * the app has zero role links — a reachable-but-powerless app is a real
 * production incident, not a cosmetic gap.
 *
 * This file is the thin orchestrator: gating, the one query that feeds the
 * table (`GET /admin/apps` — its `role_link_count`/`catalog_role_count`/
 * `catalog_last_synced_at`/`catalog_last_sync_status` are server-computed
 * aggregates, see `AdminRegistryApp` in `@/api/console`), the toolbar's
 * filter/sort view model (`useAppsTable`), and wiring the table + the four
 * modals together. Table rendering, the create form, and each modal's detail
 * live under `./apps/` — see those files for what this doc comment doesn't
 * repeat.
 *
 * EAA-1817: the previous version of this page ALSO ran a `/role-maps` query
 * (to count role links per app client-side) and one `/admin/app-roles`
 * fetch PER ROW (to find each app's most recent catalog sync) — both were
 * workarounds for aggregates the registry endpoint didn't carry yet. They
 * are gone: `role_link_count`/`catalog_role_count`/`catalog_last_synced_at`/
 * `catalog_last_sync_status` now come straight off each row.
 *
 * Gating (§1b, fail-closed): view → this page + the query below; manage →
 * the create button, empty-state CTA, and per-row/onboarding actions are
 * absent from the DOM entirely (hidden, not disabled) — see the
 * `canManage &&` guards below. The catalog dry-run preview is gated by
 * `console.rolemap.sync` instead of `console.apps.*`: it calls the exact
 * same `/role-map/sync` endpoint `/console/role-map`'s live sync button
 * does, which the server checks under that capability, not this page's.
 */
export function AppsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();

    const [pendingFlip, setPendingFlip] = useState<AdminRegistryApp | null>(null);
    const [onboardingApp, setOnboardingApp] = useState<AdminRegistryApp | null>(null);
    const [isSyncPreviewOpen, setIsSyncPreviewOpen] = useState(false);
    // EAA-1841: the app whose catalog a new role is being created in — `null`
    // keeps `CreateAppRoleModal` closed.
    const [roleTargetApp, setRoleTargetApp] = useState<AdminRegistryApp | null>(null);
    // EAA-1846: the app whose permission-group catalog is being viewed — `null`
    // keeps `AppPermissionGroupsModal` closed.
    const [permissionGroupsApp, setPermissionGroupsApp] = useState<AdminRegistryApp | null>(null);

    const viewEnabled = authenticated && Boolean(accessToken) && can('console.apps.view');
    const canManage = can('console.apps.manage');
    const canPreviewSync = can('console.rolemap.sync');

    const appsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(config.apiBase, accessToken as string),
        enabled: viewEnabled,
        retry: false,
    });

    const apps = appsQuery.data ?? [];
    const vm = useAppsTable({ apps });

    const columnCount = APPS_TABLE_BASE_COLUMNS + (canManage ? 1 : 0);

    // EAA-1846 (frame 95): breadcrumb moved to the shared topbar. Called
    // unconditionally (Rules of Hooks) — ahead of the view-capability early
    // return below, since a hook cannot follow one.
    useConsoleBreadcrumb([
        { label: t('console.apps.page.breadcrumbParent') },
        { label: t('console.apps.page.title') },
    ]);

    // EAA-1819 (fail-closed, §1b): unmount entirely without view capability —
    // the query above already resolves `enabled: false` for this user, this
    // just stops the page from rendering anything at all.
    if (!can('console.apps.view')) {
        return null;
    }

    // EAA-1846: "Thêm ứng dụng" moved from a modal (`CreateAppModal`, now
    // deleted — it had no other consumer) to a routed page at /console/apps/new,
    // per the updated design frame. Navigate rather than open state.
    function goToCreatePage() {
        navigate('/console/apps/new');
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit">
            <PageHeader
                title={t('console.apps.page.title')}
                subtitle={
                    <Trans i18nKey="console.apps.page.subtitle" components={{ bold: <strong /> }} />
                }
                actions={
                    <>
                        {/* EAA-1817: kit-component-compliance sweep — swap the raw
                            <button>s for the kit `Button` (see CLAUDE.md's mandatory
                            rule); the primary create action keeps `type="primary"`. */}
                        {canPreviewSync && (
                            <Button onClick={() => setIsSyncPreviewOpen(true)}>
                                {t('console.apps.page.previewSyncButton')}
                            </Button>
                        )}
                        {canManage && (
                            <Button type="primary" onClick={goToCreatePage}>
                                {t('console.apps.page.addAppButton')}
                            </Button>
                        )}
                    </>
                }
            />

            <div className="fit-grow">
                {appsQuery.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">{t('common.loading')}</span>
                        <SkeletonTable rows={5} columns={columnCount} />
                    </div>
                )}
                {appsQuery.isError && (
                    <ErrorState
                        description={t('console.apps.page.errorDescription')}
                        onRetry={() => appsQuery.refetch()}
                        retrying={appsQuery.isFetching}
                    />
                )}

                {appsQuery.isSuccess && (
                    <SectionCard title={t('console.apps.page.sectionTitle')}>
                        <AppsToolbar vm={vm} />

                        {apps.length === 0 ? (
                            <EmptyState
                                title={t('console.apps.page.emptyStateTitle')}
                                action={
                                    canManage && (
                                        <Button type="primary" onClick={goToCreatePage}>
                                            {t('console.apps.page.addAppButton')}
                                        </Button>
                                    )
                                }
                            />
                        ) : vm.visibleApps.length === 0 ? (
                            <EmptyState
                                title={t('console.apps.page.emptyStateNoResultsTitle')}
                                description={t('console.apps.page.emptyStateNoResultsDescription')}
                            />
                        ) : (
                            <AppsTable
                                apps={vm.visibleApps}
                                vm={vm}
                                canManage={canManage}
                                onRequestFlip={setPendingFlip}
                                onOpenOnboarding={setOnboardingApp}
                                onCreateRole={setRoleTargetApp}
                                onViewPermissionGroups={setPermissionGroupsApp}
                            />
                        )}
                    </SectionCard>
                )}
            </div>

            {/* EAA-1819/EAA-1817: hidden, not disabled, without manage — these
                modals (and their triggers above) simply don't mount. */}
            {canManage && (
                <FlipAppModal
                    app={pendingFlip}
                    roleLinkCount={pendingFlip?.role_link_count ?? 0}
                    onClose={() => setPendingFlip(null)}
                />
            )}
            {canManage && (
                <AppOnboardingModal
                    app={onboardingApp}
                    canManage={canManage}
                    onClose={() => setOnboardingApp(null)}
                />
            )}
            {canManage && (
                <CreateAppRoleModal app={roleTargetApp} onClose={() => setRoleTargetApp(null)} />
            )}
            {canManage && (
                <AppPermissionGroupsModal
                    app={permissionGroupsApp}
                    onClose={() => setPermissionGroupsApp(null)}
                />
            )}
            {canPreviewSync && (
                <CatalogSyncPreviewModal
                    open={isSyncPreviewOpen}
                    onClose={() => setIsSyncPreviewOpen(false)}
                />
            )}
        </main>
    );
}
